import { useEffect, useState, useMemo } from "react";
import "@/styles/Layout.scss";
import { getUser } from "@/hooks/useLocalStorage";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import convertTimestamp from "@/scripts/convertTimestamp";
import getProducts from "@/scripts/getProducts";
import getSellerRequests from "@/scripts/getSellerRequests";
import { ListBulletIcon } from "@radix-ui/react-icons";
import getConstants from "@/scripts/getConstants";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

function ProductExplorer() {
  const user = getUser();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  // Filters
  const [showFilter, setShowFilter] = useState(true);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    grade: "",
    form: "",
  });
  const [filteredList, setFilteredList] = useState([]);

  // Define columns with useMemo
  const columnsBuyer = useMemo(() => {
    const hasQuantityData = filteredList.some(
      (item) =>
        item.product?.quantity !== undefined && item.product?.quantity !== null
    );

    const hasPincodeData = filteredList.some(
      (item) =>
        item.product?.postalCode !== undefined &&
        item.product?.postalCode !== null
    );

    const baseColumns = [
      {
        accessorKey: "sno",
        header: "Sno",
        cell: (info) => info.row.index + 1, // Automatically generate Sno
      },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: (info) => convertTimestamp(info.getValue()),
      },
      {
        accessorKey: "product.grade",
        header: "Grade",
      },
      {
        accessorKey: "product.form",
        header: "Form",
      },
      {
        accessorKey: "actions",
        header: () => <div className="text-center">Actions</div>,
        cell: (info) => (
          <div className="text-center">
            <Button
              className="px-6 rounded-full"
              onClick={() => viewProduct(info.row.original)}
            >
              View
            </Button>
          </div>
        ),
      },
    ];

    if (hasQuantityData) {
      baseColumns.splice(4, 0, {
        accessorKey: "quantity",
        header: "Quantity",
        cell: (info) => info.row.original.product?.quantity,
      });
    }

    if (hasPincodeData) {
      baseColumns.splice(4, 0, {
        accessorKey: "pincode",
        header: "Pincode",
        cell: (info) => info.row.original.product?.postalCode,
      });
    }

    return baseColumns;
  }, [filteredList]);

  const columnsSeller = [
    {
      accessorKey: "reference_id",
      header: "Reference ID",
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: (info) => convertTimestamp(info.getValue()),
    },
    {
      accessorKey: "product.grade",
      header: "Grade",
    },
    {
      accessorKey: "product.form",
      header: "Form",
    },
    {
      accessorKey: "status",
      header: () => <div className="text-center">Status</div>, // Center-align header
      cell: (info) => {
        const status = info.getValue() || "processing";
        return (
          <div className="flex justify-center">
            <div className={`status ${status}`}>
              {capitalizedStatus(status)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "actions",
      header: () => <div className="text-center">Actions</div>, // Center-align header
      cell: (info) => (
        <div className="flex justify-center">
          <Button
            className="px-6 rounded-full"
            onClick={() => viewProduct(info.row.original)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns: user.role === "seller" ? columnsSeller : columnsBuyer,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
  });

  const viewProduct = (item) => {
    navigate("/product", { state: { data: item } });
  };

  const capitalizedStatus = (status) => {
    if (status === "processing") {
      return "Under Review";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getData = async () => {
    try {
      let data;
      setLoading(true);

      if (user.role === "buyer") {
        data = await getProducts();
      } else if (user.role === "seller") {
        data = await getSellerRequests();
      }

      setList(data);
      setFilteredList(data);
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  // Filter logic
  const filterProducts = () => {
    const filtered = list.filter((product) => {
      // Convert Firestore Timestamp to JavaScript Date
      const productDate = new Date(product.created_at);

      // Parse startDate and endDate
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const matchDate =
        (!startDate || productDate >= startDate) &&
        (!endDate ||
          productDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999)));

      const matchGrade =
        filters.grade && filters.grade !== "All"
          ? product.product.grade === filters.grade
          : true;
      const matchForm =
        filters.form && filters.form !== "All"
          ? product.product.form === filters.form
          : true;

      return matchDate && matchGrade && matchForm;
    });

    setFilteredList(filtered); // Update the filteredList state
  };

  const fetchConstants = async () => {
    try {
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    }
  };

  useEffect(() => {
    getData();
    fetchConstants();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [filters]);

  if (loading || !grades || !forms) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper mobileTableWrapper">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-green ml-0.5">
            {showFilter ? "Product Filter" : "Product Form"}
          </h1>
          <div className="highlight mb-3" />
        </div>

        <Button onClick={() => setShowFilter(!showFilter)}>
          <ListBulletIcon />
          Filters
        </Button>
      </div>

      {showFilter && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Grade Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">Grade</label>
              <Select
                onValueChange={(value) => handleFilterChange("grade", value)}
                value={filters.grade}
              >
                <SelectTrigger className="w-full">
                  <span>{filters.grade || "Select Grade"}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Grades</SelectItem>
                  {grades.map((grade, i) => (
                    <SelectItem key={i} value={grade}>
                      {grade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Form Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">Form</label>
              <Select
                onValueChange={(value) => handleFilterChange("form", value)}
                value={filters.form}
              >
                <SelectTrigger className="w-full">
                  <span>{filters.form || "Select Form"}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Forms</SelectItem>
                  {forms.map((form, i) => (
                    <SelectItem key={i} value={form}>
                      {form}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
          </div>

          <div>
            <h1 className="text-sm font-bold text-green ml-0.5">
              Product Form
            </h1>
            <div className="highlight mb-3" />
          </div>
        </>
      )}

      {/* Table */}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="w-[80px]">
              <span className="text-sm">
                {table.getState().pagination.pageSize}
              </span>
            </SelectTrigger>
            <SelectContent className="!w-[80px]">
              {[10, 25, 50].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  Show {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default ProductExplorer;
